import { useTheme } from '@ethings/ui-toolkit';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useMemo } from 'react';

/**
 * Options for configuring responsive text behavior
 */
export interface ResponsiveTextOptions {
  /** Minimum font size (e.g., '0.65rem') */
  minFontSize?: string;
  /** Maximum font size (e.g., '1rem') */
  maxFontSize?: string;
  /** Scale factor for mobile screens (default: 0.85) */
  scaleFactor?: number;
  /** Text length threshold before applying length-based scaling (default: 25) */
  textLengthThreshold?: number;
}

/**
 * Result returned by useResponsiveText hook
 */
export interface ResponsiveTextResult {
  /** Calculated responsive font size */
  fontSize: string;
  /** Line height for text */
  lineHeight: number;
  /** Word break strategy */
  wordBreak: 'normal' | 'break-word' | 'break-all';
  /** White space handling */
  whiteSpace: 'normal' | 'nowrap' | 'pre-wrap';
  /** Overflow behavior */
  overflow: 'visible' | 'hidden';
  /** Text overflow strategy */
  textOverflow: 'clip' | 'ellipsis';
}

/**
 * Hook for creating responsive text styles based on screen size and text length
 *
 * @param baseSize - Base font size (e.g., '1rem', '16px')
 * @param textLength - Optional length of text content for adaptive scaling
 * @param options - Optional configuration for responsive behavior
 * @returns Responsive text style properties
 *
 * @example
 * ```tsx
 * const textLength = innerText(title).length;
 * const responsive = useResponsiveText('1rem', textLength, {
 *   minFontSize: '0.8rem',
 *   maxFontSize: '1rem',
 *   textLengthThreshold: 25
 * });
 *
 * <Typography sx={{
 *   fontSize: responsive.fontSize,
 *   lineHeight: responsive.lineHeight,
 *   wordBreak: responsive.wordBreak
 * }}>
 *   {title}
 * </Typography>
 * ```
 */
export const useResponsiveText = (
  baseSize: string,
  textLength?: number,
  options: ResponsiveTextOptions = {},
): ResponsiveTextResult => {
  const theme = useTheme();

  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm')); // < 600px
  const isMediumScreen = useMediaQuery(theme.breakpoints.between('sm', 'md')); // 600-959px

  const {
    minFontSize,
    maxFontSize,
    scaleFactor = 0.85,
    textLengthThreshold = 25,
  } = options;

  const responsiveStyles = useMemo(() => {
    const parseFontSize = (size: string): number => {
      const match = size.match(/^([\d.]+)(rem|px|em)$/);
      if (!match) return 16; // fallback

      const value = parseFloat(match[1]);
      const unit = match[2];

      if (unit === 'rem' || unit === 'em') {
        return value * 16;
      }
      return value;
    };

    let fontSize = parseFontSize(baseSize);

    if (isSmallScreen) {
      fontSize *= scaleFactor;
    } else if (isMediumScreen) {
      fontSize *= (scaleFactor + (1 - scaleFactor) / 2);
    }

    if (textLength !== undefined && textLength > textLengthThreshold) {
      const excessLength = textLength - textLengthThreshold;
      const lengthScaleFactor = Math.max(0.6, 1 - excessLength / 80);
      fontSize *= lengthScaleFactor;
    }

    if (minFontSize) {
      const minSize = parseFontSize(minFontSize);
      fontSize = Math.max(fontSize, minSize);
    }

    if (maxFontSize) {
      const maxSize = parseFontSize(maxFontSize);
      fontSize = Math.min(fontSize, maxSize);
    }

    const fontSizeRem = `${(fontSize / 16).toFixed(3)}rem`;

    const lineHeight = isSmallScreen ? 1.2 : isMediumScreen ? 1.3 : 1.43;
    const wordBreak = isSmallScreen ? ('break-word' as const) : ('normal' as const);
    const whiteSpace = isSmallScreen ? ('normal' as const) : ('nowrap' as const);
    const overflow = isSmallScreen ? ('visible' as const) : ('hidden' as const);
    const textOverflow = isSmallScreen ? ('clip' as const) : ('ellipsis' as const);

    return {
      fontSize: fontSizeRem,
      lineHeight,
      wordBreak,
      whiteSpace,
      overflow,
      textOverflow,
    };
  }, [baseSize, textLength, isSmallScreen, isMediumScreen, minFontSize, maxFontSize, scaleFactor, textLengthThreshold]);

  return responsiveStyles;
};
